import {
  // Section icons
  Compass, Sliders, Gamepad2, Server, History, Leaf,
  // Top-level + leaf icons (matches today's AppShell imports
  // verbatim where the route already had an icon assigned).
  LayoutDashboard, MapPin, Activity, BarChart3,
  Users, CreditCard, Tag, Ticket, Radio, BadgeCheck,
  Trophy, Award, Coins, Heart, Flame, Target, Sparkles, AlertTriangle,
  ShieldCheck, Key, FileText, Settings,
  // Parent-children icons that didn't exist in today's nav (children
  // weren't surfaced in the rail). Picked to read meaningfully in a
  // 240/220 flyout panel where the icon flanks a label.
  Building2, Plug, UserCog, User, Layers, UserX,
  DollarSign, Banknote, Truck, Zap,
  Handshake, AlertCircle, Contact, MessagesSquare, BadgePercent, ClipboardList, BookOpen,
  Megaphone, Inbox, MapPinPlus, Newspaper, Rss,
  type LucideIcon,
  Wallet, BellRing, Landmark, KeyRound, Wrench,
} from 'lucide-react';

/**
 * Nav configuration for the icon-rail + flyout shell (Phase 1).
 *
 * Single source of truth for the admin navigation taxonomy. The
 * shell components in components/ui/nav/ render exclusively from
 * this data — no inline route lists, no per-component knowledge
 * of which leaves live under which section.
 *
 * Schema
 * ──────
 *   NavLeaf   — terminal nav item with a route. Always rendered
 *               with an icon flanking a label (in flyouts) or as
 *               a rail icon (when promoted to top-level inside a
 *               section's items array).
 *   NavParent — non-routable container that opens a second-level
 *               flyout cascade containing NavLeaf children. The
 *               parent's own click does NOT navigate; it toggles
 *               the cascade. (Today's behavior was to expand
 *               inline; the new shell uses cascading flyouts.)
 *   NavItem   — discriminated union NavLeaf | NavParent.
 *   NavSection — top-level group surfaced as a rail icon. The
 *               'recents' section has empty items (rendered
 *               dynamically from localStorage by the shell).
 *
 * Future extension noted in the schema (commented in NavLeaf):
 * a `requiresSuperAdmin` flag for client-side route hiding once
 * a route warrants it. Today no route does — gating is server-
 * side via the matrix-permission check.
 *
 * Icon choices
 * ────────────
 * Leaf and parent icons mirror today's AppShell imports verbatim
 * where the route already had an icon assigned. Section icons are
 * new (the old shell didn't have section-level glyphs):
 *   Overview     → Compass   (alternative: LayoutDashboard, but
 *                              that's already the Dashboard leaf)
 *   Manage       → Sliders   (alternative: ClipboardList — Sliders
 *                              read better as "operational levers")
 *   Gamification → Gamepad2  (alternative: Trophy — already used
 *                              by the Leaderboard leaf)
 *   System       → Server    (alternative: Wrench, Settings2 — Settings
 *                              is already a leaf, Wrench reads as ops
 *                              not platform)
 *   Recents      → History   (no alternative considered; literal fit)
 *
 * New child icons under parent items (children weren't surfaced
 * in today's rail, so these are fresh picks):
 *   Locations → Building2   (parent uses MapPin for the geographic
 *                             grouping; child uses Building2 for the
 *                             physical-site framing)
 *   Chargers  → Plug
 *   Team      → UserCog     (admin-side users with role assignment)
 *   Drivers   → User        (drivers — singular individual)
 *   Customer Groups → Layers (alternative: Users2; Layers reads as
 *                             "grouping of users" more clearly)
 *   Locked-out drivers → UserX
 *   Revenue   → DollarSign
 *   Payouts   → Banknote    (alternative: ArrowDownToLine; Banknote
 *                             reads as "money paid out" cleaner)
 *   Transactions → Receipt
 *   Card Management → Radio (matches today's RFID parent icon)
 *   Assignments → Link2     (alternative: UserCheck; Link2 reads as
 *                             "card-to-driver linkage")
 */

/**
 * Persona-aware visibility (Building Admin Reports commit 2 —
 * generalized from the previous per-template flag pairs
 * salesAgentVisible/Only, fleetAdminVisible, and requiresSuperAdmin
 * into a single visibleFor / hiddenFrom list pair).
 *
 *   visibleFor — when set, the filter restricts visibility per the
 *                "limited persona" rules below. Omit to mean "no
 *                explicit restriction" (default-tier admins see the
 *                item; limited personas don't).
 *   hiddenFrom — explicit blocklist. The named personas never see
 *                the item, regardless of visibleFor. Useful when
 *                an item is broadly visible but a specific persona
 *                must be excluded.
 *
 * Filter semantics (in apps/admin/src/config/visibilityFilter.ts):
 *
 *   - LIMITED personas (today: fleet_admin, sales_agent) get a
 *     RESTRICTIVE-LIST treatment — they only see items whose
 *     visibleFor explicitly includes their PersonaId. Preserves
 *     the prior "fleet_admin sees only fleetAdminVisible items;
 *     SP sees only salesAgentVisible items" behaviour.
 *   - DEFAULT-tier personas (super_admin, tenant_admin, operator,
 *     viewer, driver) get an ALLOW-BY-DEFAULT treatment — they see
 *     every item EXCEPT those whose visibleFor is set and doesn't
 *     include them (e.g. super_admin-only items hide from
 *     tenant_admin).
 *   - hiddenFrom is checked first and always wins.
 *
 * Adding a new limited persona (e.g. building_admin when its nav
 * entries land in commit 7) means (a) adding it to the
 * LIMITED_PERSONAS array in visibilityFilter.ts and (b) tagging
 * the items it should see with visibleFor entries that include it.
 */
import type { PersonaId } from '@evi/shared';
import { NETWORK_CHARGERS_HREF } from '../lib/chargerScope';

// Keep placeholder routes reachable by URL while hiding their menu links.
export const HIDDEN_NAV_HREFS: readonly string[] = ['/audit', '/rfid/assignments'];
export const DEPLOYMENT_COMMISSIONING_LABEL = 'Deployment Commissioning';

export interface NavLeaf {
  kind: 'leaf';
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Personas this item is visible to. Semantics depend on the
   *  caller's persona — see the long-form comment above for the
   *  full filter rules. */
  visibleFor?: ReadonlyArray<PersonaId>;
  /** Personas this item is hidden from regardless of visibleFor.
   *  Checked first. */
  hiddenFrom?: ReadonlyArray<PersonaId>;
}

export interface NavParent {
  kind: 'parent';
  id: string;
  label: string;
  icon: LucideIcon;
  children: NavLeaf[];
  /** Same semantic as NavLeaf.visibleFor — when set, the parent
   *  itself is gated. Children carry their own visibleFor; a parent
   *  with at least one visible child renders even without its own
   *  visibleFor (the filter walks children when the parent has no
   *  explicit visibility tag). */
  visibleFor?: ReadonlyArray<PersonaId>;
  hiddenFrom?: ReadonlyArray<PersonaId>;
}

export type NavItem = NavLeaf | NavParent;

/** The Super Admin's own sections (Warren, 21 October 2026, item M; config/superAdminNav.ts). */
export type SuperAdminSectionId = 'today' | 'network' | 'sales' | 'supply' | 'money' | 'people' | 'marketing' | 'settings';
export type NavSectionId = 'overview' | 'manage' | 'gamification' | 'system' | 'evi' | 'recents' | SuperAdminSectionId;

export interface NavSection {
  id: NavSectionId;
  label: string;
  icon: LucideIcon;
  /** Empty for 'recents' — its contents come from the recents
   *  localStorage layer, rendered dynamically by RecentsFlyout. */
  items: NavItem[];
}

/**
 * The building staff menu (Warren, 28 Oct 2026, item 5): a login a site
 * invited from its location page sees its sites' chargers and status, and
 * nothing else. Chosen by PublicUser.facilities_only in TopNav
 * and MobileNavOverlay, ahead of the persona filter: a building staff login
 * holds the Property Manager role, whose Site menu reaches money.
 */
export const BUILDING_STAFF_NAV: NavSection[] = [
  {
    id: 'overview',
    label: 'My chargers',
    icon: Plug,
    items: [{ kind: 'leaf', id: 'facilitiesSites', label: 'My chargers', href: '/facilities', icon: Plug }],
  },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Compass,
    items: [
      // Dashboard is visible to SP AND to every default-tier
      // admin. Pre-generalization it carried salesAgentVisible:true
      // with no salesAgentOnly flag — so SP saw it (via the SP
      // reductive filter) and non-SP roles also saw it (the flag
      // didn't hide them). Post-generalization: visibleFor includes
      // sales_agent so SP's restrictive-list path admits the item,
      // and includes the default tiers explicitly so the absence of
      // a hiddenFrom doesn't matter. App.tsx render branch picks
      // SalesPartnerDashboardPage for SP at /dashboard; non-SP gets
      // the operator dashboard.
      {
        kind: 'leaf', id: 'dashboard', label: 'Dashboard',
        href: '/dashboard', icon: LayoutDashboard,
        // fleet_admin since 2026-09-19: dashboardScope has a fleet branch,
        // so the operator dashboard reads the fleet's own sessions.
        //
        // 'sales_agent' REMOVED 2026-09-24. It shares this leaf with
        // every default tier, so renaming it to 'My Installs' for the
        // SP would have renamed Dashboard for operators too. The SP now
        // has its own leaf below, pointing at the same href — App.tsx
        // already branches on is_sales_agent there, so no routing moves.
        visibleFor: ['super_admin', 'tenant_admin', 'operator', 'viewer', 'fleet_admin'],
      },
      // The SP's home, renamed per Warren's ruling: a sales partner
      // thinks in installs, not dashboards. Same href as the leaf
      // above — /dashboard renders SalesPartnerDashboardPage for an SP
      // via the is_sales_agent branch in App.tsx — and the two are
      // never in one menu, because their visibleFor lists are disjoint.
      {
        kind: 'leaf', id: 'spMyInstalls', label: 'My Installs',
        href: '/dashboard', icon: LayoutDashboard,
        visibleFor: ['sales_agent'],
      },
      // Commissions, promoted out of the Reports tab set (Warren's
      // ruling). It was the first of five tabs inside
      // SalesPartnerReportsPage and reachable only by landing on
      // Reports; an SP's commission is the thing they open the admin
      // for, so it gets its own address. The legacy ?tab=commission URL
      // redirects here (App.tsx), mirroring the /payouts precedent.
      {
        kind: 'leaf', id: 'spCommissions', label: 'Commissions',
        href: '/commissions', icon: DollarSign,
        visibleFor: ['sales_agent'],
      },
      // Fleet Operator (fleet_admin) 'My fleet' — a separate leaf because
      // it points at the fleet's own page (the per-fleet Dashboard tab on
      // FleetDetailPage); the operator Dashboard above is ALSO theirs
      // since 2026-09-19 (Dashboard:read in the seed, fleet branch in
      // dashboardScope; docs/tickets/fleet-admin-dashboard-scope.md).
      // NavLeaf.href is static,
      // so this can't carry the fleet id: it points at /fleets, and
      // FleetsListPage forwards a fleet_admin with exactly one fleet to
      // /fleets/<id>; with 2+ fleets the list is the picker. Sits right
      // after the operator leaf so it is FIRST in the fleet_admin nav.
      // ── The Fleet menu ───────────────────────────────────────────
      // The framework's seven, per docs/pricing/
      // ADMIN_NAVIGATION_FRAMEWORK.md:67-76.
      //
      // Every one of these already existed as a TAB on FleetDetailPage
      // — eleven of them, sharing one address, because the tab lived in
      // useState and nothing read a URL. So the fleet menu was two
      // leaves that both resolved to /fleets and both highlighted
      // active at once, and the seven things a fleet admin actually
      // does were invisible until they found the tab bar.
      //
      // The /fleet/* paths resolve to the caller's own fleet in
      // App.tsx (one fleet → straight there; several → the picker),
      // which is why these are stable hrefs and not per-user ids.
      {
        kind: 'leaf', id: 'fleetHome', label: 'My fleet',
        href: '/fleet', icon: LayoutDashboard,
        visibleFor: ['fleet_admin'],
      },
      {
        kind: 'leaf', id: 'fleetDrivers', label: 'Drivers',
        href: '/fleet/drivers', icon: Users,
        visibleFor: ['fleet_admin'],
      },
      // Keyfobs, vehicles and plug-and-charge are one idea to a fleet
      // manager — the credentials a driver carries — and three tabs in
      // the data model. The framework names the idea.
      {
        kind: 'leaf', id: 'fleetCredentials', label: 'Keyfobs and cars',
        href: '/fleet/keyfobs', icon: Radio,
        visibleFor: ['fleet_admin'],
      },
      {
        kind: 'leaf', id: 'fleetSessions', label: 'Sessions',
        href: '/fleet/sessions', icon: Activity,
        visibleFor: ['fleet_admin'],
      },
      {
        kind: 'leaf', id: 'fleetMoney', label: 'Money',
        href: '/fleet/money', icon: Wallet,
        visibleFor: ['fleet_admin'],
      },
      // The shared /reports, whose routes compose scopeMatchForSession
      // and so read the fleet's own sessions.
      {
        kind: 'leaf', id: 'fleetAdminReports', label: 'Reports',
        href: '/reports', icon: BarChart3,
        visibleFor: ['fleet_admin'],
      },
      {
        kind: 'leaf', id: 'fleetSettings', label: 'Settings',
        href: '/fleet/settings', icon: Settings,
        visibleFor: ['fleet_admin'],
      },
      {
        kind: 'parent',
        id: 'stations',
        label: 'Stations',
        icon: MapPin,
        children: [
          { kind: 'leaf', id: 'locations', label: 'Sites', href: '/locations', icon: Building2 },
          { kind: 'leaf', id: 'chargers', label: 'Chargers', href: '/chargers', icon: Plug },
          // Network Chargers moved to EVI › Platform (2026-09-24): it
          // reads across ALL tenants, which is platform-owner work, not
          // a tenant's own site list.
        ],
      },
      { kind: 'leaf', id: 'sessions', label: 'Sessions', href: '/sessions', icon: Activity },
      // Alerts — the framework's operator item, and the first time the
      // surface has had a nav leaf at all. /alerts already routes to
      // AlertsPage; what was missing was a permission an operator could
      // hold, because every alert route gated on Tenant, which no
      // seeded matrix grants. The Alert resource (packages/shared
      // rbac.ts) fixes that and this leaf follows it: the capability
      // rule below gates on Alert, so it lights up exactly for the
      // personas the seed grants it to and stays dark for the rest.
      //
      // Anomalies are deliberately NOT a second leaf. The framework
      // wants "alerts and anomalies together", and /anomalies is
      // already reachable for these personas on Dashboard:read — it is
      // the dashboard widget's "view all". Folding the two views into
      // this one item is the remaining half of that merge.
      { kind: 'leaf', id: 'alerts', label: 'Alerts', href: '/alerts', icon: BellRing },
      { kind: 'leaf', id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3 },
      // SP-only leaves. Final SP nav: Dashboard, My Chargers,
      // Reports, My Profile.
      //   /my-chargers → SalesPartnerChargersPage
      //   /reports     → SalesPartnerReportsPage (7g.4b.3 Commit 4b)
      //   /me          → SalesPartnerProfilePage; /profile from
      //                  the user-chip menu also redirects here
      //                  for SP users (App.tsx).
      //
      // /payouts URL still redirects to /reports?tab=payouts so
      // deep links stay alive (App.tsx).
      //
      // visibleFor: ['sales_agent'] gates these to SP exclusively —
      // SP's restrictive-list filter admits them, default-tier
      // admins are excluded because visibleFor is set and doesn't
      // include them.
      {
        kind: 'leaf', id: 'spMyChargers', label: 'My Chargers',
        href: '/my-chargers', icon: Plug,
        visibleFor: ['sales_agent'],
      },
      {
        kind: 'leaf', id: 'spReports', label: 'Reports',
        href: '/reports', icon: BarChart3,
        visibleFor: ['sales_agent'],
      },
      // ── The Site menu ────────────────────────────────────────────
      // Six leaves, shared by all three Site templates
      // (building_admin, charger_owner, property_manager) via the
      // 'site' entry in PERSONA_MENU. Per
      // docs/pricing/ADMIN_NAVIGATION_FRAMEWORK.md: "A property manager
      // should be able to do their whole job here without asking anyone
      // what a tariff is."
      //
      // Before this, the three templates got three DIFFERENT accidental
      // menus — 12, 9 and 4 leaves — because the nav resolved them to
      // the 'third_party_admin' session tier and fell through to the
      // capability branch. `buildingAdminReports` was tagged
      // visibleFor: ['building_admin'] and could never render, because
      // no code path produced that PersonaId.
      {
        kind: 'leaf', id: 'siteDashboard', label: 'Dashboard',
        href: '/dashboard', icon: LayoutDashboard,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      {
        kind: 'leaf', id: 'siteHome', label: 'My Sites',
        href: '/my-site', icon: Building2,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      {
        kind: 'leaf', id: 'siteEarnings', label: 'Earnings',
        href: '/earnings', icon: Banknote,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      // OwnerPayoutsPage: payout setup plus each location's per-session
      // transfer statement. One leaf for all three templates; the page and
      // /me/bank-readiness show each persona only its own destinations.
      {
        kind: 'leaf', id: 'sitePayouts', label: 'Payouts',
        href: '/owner-payouts', icon: Landmark,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      // Shares /reports with the operator + SP entries; App.tsx's
      // persona-aware branch picks BuildingAdminReportsPage.
      {
        kind: 'leaf', id: 'siteReports', label: 'Reports',
        href: '/reports', icon: BarChart3,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      {
        kind: 'leaf', id: 'siteSettings', label: 'Settings',
        href: '/site-settings', icon: Settings,
        visibleFor: ['building_admin', 'charger_owner', 'property_manager'],
      },
      {
        kind: 'leaf', id: 'spProfile', label: 'My Profile',
        href: '/me', icon: User,
        visibleFor: ['sales_agent'],
      },
    ],
  },
  {
    id: 'manage',
    label: 'Manage',
    icon: Sliders,
    items: [
      {
        kind: 'parent',
        id: 'users',
        label: 'Users',
        icon: Users,
        children: [
          { kind: 'leaf', id: 'team', label: 'Team', href: '/users', icon: UserCog },
          { kind: 'leaf', id: 'drivers', label: 'Drivers', href: '/drivers', icon: User },
          { kind: 'leaf', id: 'groups', label: 'Customer Groups', href: '/groups', icon: Layers },
          { kind: 'leaf', id: 'lockedOut', label: 'Locked-out drivers', href: '/users/locked-out', icon: UserX },
        ],
      },
      // Money — ONE leaf with tabs (Warren's ruling 3, 2026-09-24),
      // replacing the Billing parent's three children. The framework's
      // operator menu says "Payouts, revenue, subscription, invoices.
      // Tabs, one item." Orders joins them from the Sales group below,
      // so the four money questions live in one place instead of two
      // menus and four clicks.
      //
      // The old hrefs still route — /billing/revenue, /billing/payouts,
      // /transactions and /orders all keep their pages — so bookmarks
      // and any in-app navigate() calls survive; they simply have no
      // nav leaf of their own any more.
      { kind: 'leaf', id: 'money', label: 'Money', href: '/money', icon: CreditCard },
      // Today, Approvals (Warren, 29 October 2026, item 2): shown to a holder of an approver type (approve:*, the
      // strings the Approvals routes check, on /auth/me), whatever the persona (visibilityFilter.ts 'held').
      { kind: 'leaf', id: 'approvals', label: 'Approvals', href: '/approvals', icon: BadgeCheck },
      // RFID moved above Tariffs/Coupons/Fleets — sectioned parents
      // (Users, Billing, RFID) cluster at the top of the Manage menu;
      // unsectioned leaves (Tariffs, Coupons, Fleets) drop to the
      // bottom. Reads cleaner across both the desktop dropdown
      // accordion and the mobile drawer.
      {
        kind: 'parent',
        id: 'rfid',
        label: 'RFID',
        icon: Radio,
        children: [
          { kind: 'leaf', id: 'rfidCards', label: 'Card Management', href: '/rfid', icon: Radio },
        ],
      },
      // Announcements, the driver-PWA popup CMS. The Communications group
      // is gone (Warren, 23 October 2026): super_admin finds Announcements
      // under EVI > Marketing, and every other tier keeps it here, exactly
      // as before (no visibleFor; the seed grants operator and
      // tenant_admin announcement:read/write).
      { kind: 'leaf', id: 'announcements', label: 'Announcements', href: '/announcements', icon: Megaphone,
        hiddenFrom: ['super_admin'] },
      { kind: 'leaf', id: 'tariffs', label: 'Tariffs', href: '/tariffs', icon: Tag },
      { kind: 'leaf', id: 'coupons', label: 'Coupons', href: '/coupons', icon: Ticket },
      // Registration and commissioning stay together on desktop and mobile.
      // Partners register work through New job under Jobs (Warren, 20 October 2026): the Register Install leaf is
      // gone and /register-install redirects partners to /sales-jobs/new.
      // The deployment registration surface. Limited personas see only
      // explicitly-listed leaves, so this is the fleet-manager door
      // into the flow. Staff (super_admin, tenant_admin, operator) also
      // carry it: the store order detail and the ops fulfillment email
      // send them here to register a commercial order's units. viewer is
      // read-only and does not. The tier rule lives in visibilityFilter.ts.
      {
        kind: 'leaf', id: 'submitLocation', label: 'Submit a Location',
        href: '/submit-location', icon: MapPinPlus,
        visibleFor: ['super_admin', 'tenant_admin', 'operator', 'fleet_admin'],
      },
      {
        kind: 'leaf', id: 'deployments', label: DEPLOYMENT_COMMISSIONING_LABEL,
        href: '/deployments', icon: Plug,
        hiddenFrom: ['super_admin'],
        // The API scopes this to the submitter, assigned installer or operator.
        // Site admins can also be assigned an installation to commission.
        visibleFor: ['super_admin', 'tenant_admin', 'operator', 'viewer',
          'third_party_admin', 'sales_agent', 'fleet_admin',
          'building_admin', 'charger_owner', 'property_manager'],
      },
      { kind: 'leaf', id: 'procurement', label: 'Procurement', href: '/procurement', icon: Truck,
        visibleFor: ['super_admin', 'operator'], hiddenFrom: ['super_admin'] },
      // Fleet platform — OPERATOR-facing fleet management: every fleet
      // on the tenant. 'fleet_admin' is deliberately NOT in this list
      // any more. It used to be, so a fleet admin carried two leaves
      // that both resolved to /fleets and both highlighted active at
      // once — the duplicate the nav's own comment called "accepted
      // until a per-user href exists". /fleet/* is that href, and a
      // fleet admin does not want a roster of other people's fleets.
      {
        kind: 'leaf', id: 'fleets', label: 'Fleets', href: '/fleets',
        icon: Truck,
        visibleFor: ['super_admin', 'tenant_admin', 'operator', 'viewer'],
      },
    ],
  },
  {
    id: 'gamification',
    label: 'Gamification',
    icon: Gamepad2,
    items: [
      { kind: 'leaf', id: 'leaderboard', label: 'Leaderboard', href: '/leaderboard', icon: Trophy },
      { kind: 'leaf', id: 'badges', label: 'Badges', href: '/badges', icon: Award },
      { kind: 'leaf', id: 'earningRules', label: 'Earning Rules', href: '/earning-rules', icon: Coins },
      { kind: 'leaf', id: 'friendships', label: 'Friendships', href: '/friendships', icon: Heart },
      { kind: 'leaf', id: 'streaks', label: 'Streaks', href: '/streaks', icon: Flame },
      { kind: 'leaf', id: 'challenges', label: 'Challenges', href: '/challenges', icon: Target },
      { kind: 'leaf', id: 'unlocks', label: 'Unlocks', href: '/unlocks', icon: Sparkles },
      { kind: 'leaf', id: 'antiAbuse', label: 'Anti-abuse', href: '/anti-abuse', icon: AlertTriangle },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Server,
    items: [
      { kind: 'leaf', id: 'roles', label: 'Roles & Permissions', href: '/roles', icon: ShieldCheck },
      { kind: 'leaf', id: 'access', label: 'Access Management', href: '/access', icon: Key },
      { kind: 'leaf', id: 'settings', label: 'Settings', href: '/settings', icon: Settings },
      // Carbon Credits, Sales Partners, the SP Queue, Operators, RFID
      // Pricing and Plug & Charge MOVED to the EVI section below — every
      // one already carried visibleFor:['super_admin'].
    ],
  },
  // ── EVI staff ────────────────────────────────────────────────────
  //
  // The framework's group below a divider
  // (docs/pricing/ADMIN_NAVIGATION_FRAMEWORK.md:103-111): work that
  // belongs to the platform owner rather than to an operator running a
  // tenant.
  //
  // THE DIVIDER IS FREE. filterNavSectionsForPersona drops a section
  // whose items all filter out, so for every persona but super_admin
  // this pill simply does not exist — no separator primitive, no
  // conditional rendering, and no chance of an empty group.
  //
  // EVERY LEAF HERE IS EXPLICITLY visibleFor:['super_admin']. Six of
  // them (Blog, Newsletter, Conversations, Leads, Incentives, Network
  // Submissions) were tagged super_admin+operator and were
  // super_admin-only only by ACCIDENT — they have no DEFAULT_TIER_RULES
  // entry, and an unmapped leaf fails closed. Making the tag match
  // reality costs an operator nothing (they could not open them) and
  // stops the group depending on an accident to stay correct.
  //
  // ⚠️ TWO OF THE FRAMEWORK'S FIVE ITEMS ARE NOT HERE, deliberately:
  //
  //   Store   — there is no products page and no quotes page anywhere
  //             in apps/admin/src; the catalog is a read-only API and
  //             bulk quotes are written as Leads. Orders, the only real
  //             Store object, became a Money tab. A Store group would
  //             launch with zero working leaves.
  //   Fleets  — /fleets exists and works, but tenant_admin holds
  //             fleet:read and sees it TODAY. Moving it here removes it
  //             from the thirteen tenant admins on production.
  //
  // Announcements joined Marketing for super_admin on 23 October 2026
  // (Warren); operator and tenant_admin keep their Manage leaf, because
  // the seed grants them announcement:*. Coupons stays in Manage for the
  // same reason.
  {
    id: 'evi',
    label: 'EVI',
    icon: Sparkles,
    items: [
      {
        kind: 'parent',
        id: 'eviSales',
        label: 'Sales',
        icon: Handshake,
        visibleFor: ['super_admin'],
        children: [
          { kind: 'leaf', id: 'salesStaff', label: 'Sales Staff', href: '/sales-staff', icon: UserCog,
            visibleFor: ['super_admin'] },
          { kind: 'leaf', id: 'salesPayoutRun', label: 'Payout run', href: '/sales-payout-run', icon: FileText, visibleFor: ['super_admin'] },
          { kind: 'leaf', id: 'salesTeam', label: 'Sales Team', href: '/sales-team', icon: Users, visibleFor: ['super_admin'] },
          { kind: 'leaf', id: 'salesStatements', label: 'Statements', href: '/sales-statements', icon: FileText,
            visibleFor: ['super_admin'] },
          {
            kind: 'leaf', id: 'leads', label: 'Leads', href: '/leads', icon: Contact,
            visibleFor: ['super_admin'],
          },
        ],
      },
      // Partners (Warren, 23 October 2026): the partner programme and the
      // deployment pipeline in one place. It replaces EVI > Deployments.
      // Operators and tenant admins keep Deployment Commissioning and
      // Procurement in their Manage menu (those leaves are hiddenFrom
      // super_admin there), because they never see the EVI section.
      {
        kind: 'parent', id: 'eviPartners', label: 'Partners', icon: Handshake,
        visibleFor: ['super_admin'],
        children: [
          // Sales Partners rolodex (Agents in the codebase; identifiers
          // stay 'agent' throughout). First in the group (Warren, 25
          // October 2026: "Sales Partners (/agents) goes first under EVI >
          // Partners, above Partner Applications"). The group itself is
          // Super Admin only, so an Operator's menu does not change.
          {
            kind: 'leaf', id: 'agents', label: 'Sales Partners', href: '/agents',
            icon: Handshake,
            visibleFor: ['super_admin', 'operator'],
          },
          // Applications — the "See if I qualify" review queue, which a
          // human works within one business day. There is no separate
          // applications model: a qualification submission is captured
          // by POST /public/leads with source 'sp_qualification', which
          // audienceForSource maps to the sales_partner audience and
          // leadEmails notifies lead-ops about. So the queue is the
          // Leads page with that audience seeded, behind its own
          // address — NOT a second leaf pointing at /leads.
          //
          // Deliberately distinct from its two siblings, per Warren:
          // payout exceptions are operations, location proposals are
          // network, and applications are neither. Merging any of the
          // three under one label would hide all of them.
          {
            kind: 'leaf', id: 'partnerApplications', label: 'Partner Applications',
            href: '/partner-applications', icon: Inbox,
            visibleFor: ['super_admin'],
          },
          // SP ops attention queue: AgentPayout rows in clawback_pending
          // / permanently_failed, or flagged manual_review_required.
          // Distinct from the rolodex by intent — that is CRUD on
          // partner identities, this is triage on money-flow exceptions.
          {
            kind: 'leaf', id: 'agentOpsQueue', label: 'Sales Partner Queue',
            href: '/agent-ops-queue',
            icon: AlertCircle,
            visibleFor: ['super_admin'],
          },
          // What waits on a person at EVI, with each item's age against its promise (Warren, 21 October 2026).
          // It led EVI > Deployments, which Partners replaced (23 October 2026), so it leads the deployment items here;
          // the Super Admin's own menu shows it under Supply (config/superAdminNav.ts).
          {
            kind: 'leaf', id: 'eviQueues', label: 'EVI Queues',
            href: '/evi-queues', icon: AlertCircle, visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'networkSubmissions', label: 'Network Submissions',
            href: '/network-submissions', icon: Inbox, visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'eviCommissioning', label: DEPLOYMENT_COMMISSIONING_LABEL,
            href: '/deployments', icon: Plug, visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'eviProcurement', label: 'Procurement',
            href: '/procurement', icon: Truck, visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'factorySettings', label: 'Factory Provisioning',
            href: '/factory-settings', icon: Plug, visibleFor: ['super_admin'],
          },
        ],
      },
      {
        kind: 'parent',
        id: 'eviMarketing',
        label: 'Marketing',
        icon: Megaphone,
        visibleFor: ['super_admin'],
        children: [
          // Announcements for super_admin (Warren, 23 October 2026); every
          // other tier keeps the Manage leaf.
          {
            kind: 'leaf', id: 'eviAnnouncements', label: 'Announcements', href: '/announcements', icon: Megaphone,
            visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'newsletter', label: 'Newsletter', href: '/newsletter', icon: Newspaper,
            visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'blog', label: 'Blog', href: '/blog', icon: Rss,
            visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'conversations', label: 'Conversations', href: '/conversations', icon: MessagesSquare,
            visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'incentives', label: 'Incentives', href: '/incentives', icon: BadgePercent,
            visibleFor: ['super_admin'],
          },
        ],
      },
      {
        kind: 'parent',
        id: 'eviPlatform',
        label: 'Platform',
        icon: Server,
        visibleFor: ['super_admin'],
        children: [
          // "Operator" is the display name for the Tenant record — the
          // org that owns locations, pays EVI and receives payouts. The
          // route, id and API paths keep the tenant wording. A tenant's
          // own admin manages their subscription from Money instead.
          {
            kind: 'leaf', id: 'tenants', label: 'Operators',
            href: '/tenants', icon: Building2,
            visibleFor: ['super_admin'],
          },
          // Every charger across ALL tenants, residential homes
          // included (GET /admin/chargers/oversight). One Chargers page
          // since 28 October 2026 (Warren): this row opens it on the
          // whole network, and the old /chargers/oversight redirects
          // there (lib/chargerScope.ts).
          {
            kind: 'leaf', id: 'chargerOversight', label: 'Network Chargers',
            href: NETWORK_CHARGERS_HREF, icon: Plug,
            visibleFor: ['super_admin'],
          },
          // Carbon program console: claim batches + margin, accrual
          // ledger, charger registration state.
          {
            kind: 'leaf', id: 'carbon', label: 'Carbon Credits', href: '/carbon',
            icon: Leaf,
            visibleFor: ['super_admin'],
          },
          // Site Host Terms console: manual electricity reimbursement,
          // Transition Notices, locations without a time zone.
          {
            kind: 'leaf', id: 'siteElectricity', label: 'Site Electricity', href: '/site-electricity',
            icon: Landmark,
            visibleFor: ['super_admin'],
          },
          // EVI platform-revenue levers. Both server routes gate
          // identically.
          {
            kind: 'leaf', id: 'rfidPricing', label: 'RFID Pricing',
            href: '/settings/rfid-pricing', icon: DollarSign,
            visibleFor: ['super_admin'],
          },
          {
            kind: 'leaf', id: 'autocharge', label: 'Plug & Charge',
            href: '/settings/autocharge', icon: Zap,
            visibleFor: ['super_admin'],
          },
          // Claude connections to the EVII Mcp server, with Revoke
          // (GET/POST /admin/mcp/connections gate identically:
          // super_admin only).
          {
            kind: 'leaf', id: 'mcpConnections', label: 'Connector Access',
            href: '/settings/connectors', icon: KeyRound,
            visibleFor: ['super_admin'],
          },
          // Settings, Alerts (Warren, 29 Oct 2026, item 5): quiet alerts by default, per-type switches, thresholds,
          // digest times, EVI ops recipients and a 7-day preview. GET/PUT /settings/alerts gate identically:
          // super_admin only.
          {
            kind: 'leaf', id: 'alertSettings', label: 'Alerts', href: '/settings/alerts', icon: BellRing,
            visibleFor: ['super_admin'],
          },
        ],
      },
    ],
  },
  {
    id: 'recents',
    label: 'Recents',
    icon: History,
    items: [],
  },
];

NAV_SECTIONS.find(s => s.id === 'overview')!.items.push(
  { kind: 'leaf', id: 'salesToday', label: 'Today', href: '/sales-today', icon: LayoutDashboard, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesDashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesJobs', label: 'Jobs', href: '/sales-jobs', icon: Truck, visibleFor: ['sales_agent', 'sales_partner_member', 'sales_rep', 'sales_manager', 'super_admin'] },
  // Service calls after launch (ruling H, 21 October 2026): the installer answers; EVI ops holds the rest.
  { kind: 'leaf', id: 'serviceCalls', label: 'Service calls', href: '/service-requests', icon: Wrench, visibleFor: ['sales_agent', 'sales_partner_member', 'sales_rep', 'sales_manager', 'super_admin'] },
  // The partner's saved customers (Warren, 21 October 2026). The Api decides what each viewer may do there.
  { kind: 'leaf', id: 'salesCustomers', label: 'Customers', href: '/sales-customers', icon: Contact, visibleFor: ['sales_agent', 'sales_partner_member', 'sales_rep', 'sales_manager', 'super_admin'] },
  // Quotes are optional; reps and managers see their partners' pipeline there.
  { kind: 'leaf', id: 'salesQuotes', label: 'Quotes', href: '/sales-quotes', icon: FileText, visibleFor: ['sales_agent', 'sales_partner_member', 'sales_rep', 'sales_manager', 'super_admin'] },
  // Site survey mode, the partner team's own (Warren, 21 October 2026).
  { kind: 'leaf', id: 'salesSurveys', label: 'Surveys', href: '/sales-surveys', icon: ClipboardList, visibleFor: ['sales_agent', 'sales_partner_member'] },
  // Co-branded spec sheets and the network one-pager (Warren, 21 October 2026).
  { kind: 'leaf', id: 'salesKit', label: 'Sales kit', href: '/sales-kit', icon: BookOpen, visibleFor: ['sales_agent', 'sales_partner_member'] },
  { kind: 'leaf', id: 'salesTeamView', label: 'Sales Team', href: '/sales-team', icon: Users, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesBookPartners', label: 'Partners', href: '/sales-book/partners', icon: Handshake, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesBookOrders', label: 'Orders', href: '/sales-book/orders', icon: Truck, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesBookOnboarding', label: 'Onboarding help', href: '/sales-book/onboarding', icon: Users, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesSubmitLocation', label: 'Submit a Location', href: '/submit-location', icon: MapPin, visibleFor: ['sales_rep', 'sales_manager'] },
  { kind: 'leaf', id: 'salesCommission', label: 'My commission', href: '/my-commission', icon: Banknote, visibleFor: ['sales_rep', 'sales_manager'] },
);
